import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Collapse,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {IconChevronDown, IconChevronRight, IconPlus, IconSearch, IconX} from "@tabler/icons-react";
import {RAIL, rgba} from "../rail/railTokens";

const PAGE = 5;

/**
 * A collapsible, searchable, paginated section of the flow side menu.
 * Purely presentational – parents pass the resolved `items`, a `mapItem`
 * renderer, and the search value + setter.
 */
export const ListBlock = ({items = [], title, mapItem, search, onChangeSearch, onCreate, isOpen = false}) => {
    const [open, setOpen] = useState(!!isOpen);
    const [visible, setVisible] = useState(PAGE);

    useEffect(() => {
        if (!open) setVisible(PAGE);
    }, [open]);

    const count = items?.length || 0;
    const shown = items?.slice(0, visible) || [];

    return (
        <Box
            sx={{
                px: 0.5,
                py: 0.25,
                borderBottom: `1px solid ${rgba(RAIL.accent, 0.1)}`,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                onClick={() => setOpen(!open)}
                sx={{
                    px: 0.75,
                    py: 0.5,
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: open ? RAIL.ink : RAIL.muted,
                    transition: "background-color .12s ease, color .12s ease",
                    "&:hover": {backgroundColor: rgba(RAIL.accent, 0.06), color: RAIL.ink},
                    "&:hover .list-block-add": {opacity: 1},
                }}
            >
                <IconChevronRight
                    size={14}
                    style={{transform: open ? "rotate(90deg)" : "none", transition: "transform .15s ease", flexShrink: 0}}
                />
                <Typography
                    sx={{
                        ml: 0.5,
                        flex: 1,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "inherit",
                    }}
                >
                    {title}
                </Typography>
                {count > 0 && (
                    <Box
                        sx={{
                            mr: 0.5,
                            px: 0.6,
                            minWidth: 16,
                            textAlign: "center",
                            borderRadius: "999px",
                            fontSize: 10,
                            fontWeight: 600,
                            lineHeight: "16px",
                            color: RAIL.muted,
                            backgroundColor: rgba(RAIL.accent, 0.1),
                        }}
                    >
                        {count}
                    </Box>
                )}
                <Tooltip title={`Add ${title}`} placement="right" disableInteractive>
                    <IconButton
                        className="list-block-add"
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            onCreate?.();
                        }}
                        sx={{
                            p: 0.25,
                            color: RAIL.muted,
                            opacity: open ? 1 : 0,
                            transition: "opacity .12s ease, background-color .12s ease, color .12s ease",
                            "&:hover": {backgroundColor: rgba(RAIL.accent, 0.12), color: RAIL.accent},
                        }}
                    >
                        <IconPlus size={14}/>
                    </IconButton>
                </Tooltip>
            </Stack>

            <Collapse in={open} unmountOnExit>
                <Box sx={{px: 0.75, pt: 0.5, pb: 0.25}}>
                    <TextField
                        size="small"
                        fullWidth
                        value={search}
                        placeholder="Search"
                        onChange={(event) => onChangeSearch(event.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: 12.5,
                                backgroundColor: rgba(RAIL.accent, 0.04),
                                "& fieldset": {borderColor: rgba(RAIL.accent, 0.16)},
                                "&:hover fieldset": {borderColor: rgba(RAIL.accent, 0.3)},
                                "&.Mui-focused fieldset": {borderColor: RAIL.accent, borderWidth: "1px"},
                            },
                            "& .MuiOutlinedInput-input": {py: 0.65},
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconSearch size={14}/>
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" sx={{p: 0.25}} onClick={() => onChangeSearch("")}>
                                        <IconX size={13}/>
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </Box>

                <Box sx={{px: 0.5, pb: 0.5}}>
                    {shown.map((item) => mapItem(item))}

                    {count === 0 && (
                        <Typography sx={{px: 1, py: 0.75, fontSize: 11.5, color: rgba(RAIL.muted, 0.8)}}>
                            {search ? "No matches" : `No ${title?.toLowerCase()} yet`}
                        </Typography>
                    )}

                    {count > PAGE && (
                        <Button
                            fullWidth
                            disableRipple
                            onClick={() => setVisible(visible >= count ? PAGE : count)}
                            startIcon={visible >= count ? <IconChevronDown size={13} style={{transform: "rotate(180deg)"}}/> : <IconChevronDown size={13}/>}
                            sx={{
                                mt: 0.25,
                                fontSize: 11.5,
                                fontWeight: 500,
                                textTransform: "none",
                                color: RAIL.muted,
                                borderRadius: "8px",
                                "&:hover": {backgroundColor: rgba(RAIL.accent, 0.06), color: RAIL.accent},
                            }}
                        >
                            {visible >= count ? "Show less" : `Show ${count - PAGE} more`}
                        </Button>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};
